<div class="forgot-password-modal">
  @if (isLoading) {
    <app-loader></app-loader>
  }

  <!-- Header -->
  <div class="modal-header">
    <h1 class="text-lg md:text-2xl text-gray-900">
      @switch (step) {
        @case ("email") {
          Forgot password
        }
        @case ("otp") {
          Check your email
        }
        @case ("password") {
          Create new password
        }
        @case ("success") {
          Password reset
        }
      }
    </h1>

    <button
      type="button"
      class="close-button"
      (click)="closeDialog()"
      aria-label="Close"
    >
      <img [src]="assetUrl + 'icons/x-close.svg'" alt="" />
    </button>
  </div>

  <!-- ============================= -->
  <!-- STEP 1: EMAIL -->
  <!-- ============================= -->

  @if (step === "email") {
    <div class="step-content">
      <p class="step-description">
        Enter your email and we'll send you a verification code to reset your
        password.
      </p>

      <form
        [formGroup]="forgotPasswordForm"
        (ngSubmit)="requestResetCode()"
        class="form-container"
      >
        <label class="field">
          <span>Email</span>

          <input
            type="email"
            formControlName="email"
            autocomplete="email"
            placeholder="johndoe@gmail.com"
            [class.input-error]="email?.invalid && email?.touched"
          />

          @if (email?.invalid && email?.touched) {
            <div class="validation-error">
              @if (email?.errors?.["required"]) {
                <div>Email is required.</div>
              }

              @if (email?.errors?.["email"]) {
                <div>Please enter a valid email.</div>
              }
            </div>
          }
        </label>

        @if (errorMessage) {
          <div class="api-error">
            {{ errorMessage }}
          </div>
        }

        <button
          type="submit"
          [disabled]="forgotPasswordForm.invalid || isLoading"
          class="primary-button"
        >
          {{ isLoading ? "Sending..." : "Send verification code" }}
        </button>
      </form>
    </div>
  }

  <!-- ============================= -->
  <!-- STEP 2: OTP -->
  <!-- ============================= -->

  @if (step === "otp") {
    <div class="step-content">
      <p class="step-description">
        We sent a verification code to
        <strong>{{ resetEmail }}</strong>
      </p>

      <form
        [formGroup]="otpForm"
        (ngSubmit)="verifyResetCode()"
        class="form-container"
      >
        <label class="field">
          <span>Verification code</span>

          <input
            type="text"
            formControlName="otp"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="000000"
            class="otp-input"
            [class.input-error]="otp?.invalid && otp?.touched"
          />

          @if (otp?.invalid && otp?.touched) {
            <div class="validation-error">
              @if (otp?.errors?.["required"]) {
                <div>Verification code is required.</div>
              }

              @if (otp?.errors?.["pattern"]) {
                <div>Please enter the 6-digit code.</div>
              }
            </div>
          }
        </label>

        @if (errorMessage) {
          <div class="api-error">
            {{ errorMessage }}
          </div>
        }

        <button
          type="submit"
          [disabled]="otpForm.invalid || isLoading"
          class="primary-button"
        >
          {{ isLoading ? "Verifying..." : "Verify code" }}
        </button>

        <div class="otp-actions">
          <button type="button" (click)="resendCode()" [disabled]="isLoading">
            Resend code
          </button>

          <span>•</span>

          <button type="button" (click)="changeEmail()" [disabled]="isLoading">
            Change email
          </button>
        </div>
      </form>
    </div>
  }

  <!-- ============================= -->
  <!-- STEP 3: NEW PASSWORD -->
  <!-- ============================= -->

  @if (step === "password") {
    <div class="step-content">
      <p class="step-description">Choose a new password for your account.</p>

      <form
        [formGroup]="passwordForm"
        (ngSubmit)="resetPassword()"
        class="form-container"
      >
        <!-- New password -->
        <label class="field">
          <span>New password</span>

          <div class="password-input-container">
            <input
              [type]="passwordVisible ? 'text' : 'password'"
              formControlName="password"
              autocomplete="new-password"
              placeholder="Enter new password"
              [class.input-error]="password?.invalid && password?.touched"
            />

            <button
              type="button"
              class="visibility-button"
              (click)="togglePasswordVisibility()"
              [attr.aria-label]="
                passwordVisible ? 'Hide password' : 'Show password'
              "
            >
              <svg
                *ngIf="!passwordVisible"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>

              <svg
                *ngIf="passwordVisible"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.3-4.2M6.423 6.423A9.953 9.953 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.956 9.956 0 01-1.23 2.57M15 12a3 3 0 01-4.874 2.316M3 3l18 18"
                />
              </svg>
            </button>
          </div>

          @if (password?.invalid && password?.touched) {
            <div class="validation-error">
              @if (password?.errors?.["required"]) {
                <div>Password is required.</div>
              }

              @if (password?.errors?.["minlength"]) {
                <div>Password must be at least 8 characters.</div>
              }
            </div>
          }
        </label>

        <!-- Confirm password -->
        <label class="field">
          <span>Confirm password</span>

          <div class="password-input-container">
            <input
              [type]="confirmPasswordVisible ? 'text' : 'password'"
              formControlName="confirmPassword"
              autocomplete="new-password"
              placeholder="Confirm new password"
              [class.input-error]="
                confirmPassword?.touched &&
                (confirmPassword?.invalid ||
                  passwordForm.errors?.['passwordMismatch'])
              "
            />

            <button
              type="button"
              class="visibility-button"
              (click)="toggleConfirmPasswordVisibility()"
              [attr.aria-label]="
                confirmPasswordVisible ? 'Hide password' : 'Show password'
              "
            >
              <svg
                *ngIf="!confirmPasswordVisible"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>

              <svg
                *ngIf="confirmPasswordVisible"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.3-4.2M6.423 6.423A9.953 9.953 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.956 9.956 0 01-1.23 2.57M15 12a3 3 0 01-4.874 2.316M3 3l18 18"
                />
              </svg>
            </button>
          </div>

          @if (
            confirmPassword?.touched &&
            passwordForm.errors?.["passwordMismatch"]
          ) {
            <div class="validation-error">Passwords do not match.</div>
          }
        </label>

        <p class="password-hint">Must be at least 8 characters.</p>

        @if (errorMessage) {
          <div class="api-error">
            {{ errorMessage }}
          </div>
        }

        <button
          type="submit"
          [disabled]="passwordForm.invalid || isLoading"
          class="primary-button"
        >
          {{ isLoading ? "Resetting..." : "Reset password" }}
        </button>
      </form>
    </div>
  }

  <!-- ============================= -->
  <!-- STEP 4: SUCCESS -->
  <!-- ============================= -->

  @if (step === "success") {
    <div class="success-content">
      <div class="success-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2.2"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <p>
        Your password has been successfully changed. You can now sign in using
        your new password.
      </p>

      <button type="button" class="primary-button" (click)="closeDialog()">
        Back to login
      </button>
    </div>
  }
</div>
